import { watch, computed, Ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useCustomisationDocuments } from '../api/customisation.api';
import { useCustomisationStore } from '../stores/customisation.store';

/**
 * Composable to load customisation documents and populate the store
 * Should be called once at app initialization
 *
 * @param enabled - Optional flag to control when loading should start (default: true)
 * @returns Object containing loading state, error state, and refetch function
 */
export const useLoadCustomisation = (enabled: Ref<boolean> | boolean = true) => {
  const store = useCustomisationStore();
  const { isMocked } = storeToRefs(store);

  const isEnabled = computed(() => {
    return typeof enabled === 'boolean' ? enabled : enabled.value;
  });

  // Skip API fetching if documents were provided via INIT (mocked)
  const shouldFetch = computed(() => isEnabled.value && !isMocked.value);

  const { data, isLoading, isError, error, refetch } = useCustomisationDocuments(shouldFetch);

  const hasDocuments = computed(() => (data.value?.length ?? 0) > 0);

  watch(
    data,
    (documents) => {
      // Only update store from API data if not mocked
      if (documents != null && !isMocked.value) {
        store.setDocuments(documents);
      }
    },
    { immediate: true }
  );

  // Only refetch when enabled becomes true and not mocked
  watch(
    shouldFetch,
    (canFetch) => {
      if (canFetch) {
        refetch();
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    if (shouldFetch.value) {
      refetch();
    }
  });

  return {
    hasDocuments,
    isLoading,
    isError,
    error,
    refetch,
  };
};
